import { useRef, useState } from "react";
import { catalogOf, validateLayout } from "./vanData";

// Deterministic top-down van schematic. Van length runs vertically (portrait).
// Editable: drag modules (pointer) or tap to select. Emits mm coordinates.
const MODULE_FILL = {
  sleep: "#FFE08A", kitchen: "#A4DCFA", storage: "#C9F5D2", seat: "#FFC9C9",
  utility: "#E2D4F8", work: "#FFD9A6", bath: "#B9E4FF",
};

export default function VanSchematic({
  van, modules, selectedId, onSelect, onMove, editable = true, maxW = 372, maxH = 520, showGuides = true,
}) {
  const interior = van?.interior || { length: 3200, width: 1750, height: 1850, arch_width: 1400, arch_y: [1500, 2050] };
  const pad = 8;
  const scale = Math.min((maxW - pad * 2) / interior.width, (maxH - pad * 2) / interior.length);
  const W = interior.width * scale + pad * 2;
  const H = interior.length * scale + pad * 2;
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [, force] = useState(0);

  const v = validateLayout(van, modules);
  const mm = (n) => n * scale;
  const px2mm = (n) => n / scale;

  const archX0 = (interior.width - (interior.arch_width || interior.width * 0.8)) / 2;
  const archW = interior.arch_width || interior.width * 0.8;
  const archY = interior.arch_y || [interior.length * 0.45, interior.length * 0.63];
  const sideDoor = van?.doors?.side;
  const rearDoor = van?.doors?.rear;

  const startDrag = (e, m) => {
    if (!editable) { onSelect?.(m.id); return; }
    e.preventDefault();
    onSelect?.(m.id);
    const pt = e.touches ? e.touches[0] : e;
    drag.current = { id: m.id, startX: pt.clientX, startY: pt.clientY, ox: m.x, oy: m.y };
  };
  const moveDrag = (e) => {
    if (!drag.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const dxmm = px2mm(pt.clientX - drag.current.startX);
    const dymm = px2mm(pt.clientY - drag.current.startY);
    const m = modules.find((x) => x.id === drag.current.id);
    if (!m) return;
    let nx = Math.round((drag.current.ox + dxmm) / 20) * 20;
    let ny = Math.round((drag.current.oy + dymm) / 20) * 20;
    nx = Math.max(0, Math.min(nx, interior.width - m.w));
    ny = Math.max(0, Math.min(ny, interior.length - m.h));
    onMove?.(m.id, nx, ny);
    force((n) => n + 1);
  };
  const endDrag = () => { drag.current = null; };

  return (
    <div className="w-full flex justify-center" data-testid="van-schematic">
      <svg
        ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        onPointerMove={moveDrag} onPointerUp={endDrag} onPointerLeave={endDrag}
        style={{ touchAction: "none", background: "#fff", border: "3px solid #000", borderRadius: 14 }}
      >
        {/* interior outline */}
        <rect x={pad} y={pad} width={mm(interior.width)} height={mm(interior.length)} fill="#FDFBF7" stroke="#000" strokeWidth="2" />
        {/* front / rear labels */}
        <text x={W / 2} y={pad + 12} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#000">FRONT (cab)</text>
        <text x={W / 2} y={H - pad - 4} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="#000">REAR</text>

        {showGuides && (
          <>
            {/* wheel arches */}
            <rect x={pad + mm(archX0)} y={pad + mm(archY[0])} width={mm(archW * 0)} height={mm(archY[1] - archY[0])} />
            <rect x={pad} y={pad + mm(archY[0])} width={mm(archX0)} height={mm(archY[1] - archY[0])} fill="#000" opacity="0.10" />
            <rect x={pad + mm(archX0 + archW)} y={pad + mm(archY[0])} width={mm(interior.width - archX0 - archW)} height={mm(archY[1] - archY[0])} fill="#000" opacity="0.10" />
            <text x={pad + mm(archX0) / 2} y={pad + mm((archY[0] + archY[1]) / 2)} textAnchor="middle" fontSize="7" fontFamily="monospace" fill="#000" opacity="0.6">ARCH</text>
            {/* side door (right edge) */}
            {sideDoor && (
              <line x1={pad + mm(interior.width)} y1={pad + mm(sideDoor.y[0])} x2={pad + mm(interior.width)} y2={pad + mm(sideDoor.y[1])}
                stroke="#D4AF37" strokeWidth="5" strokeDasharray="6 4" />
            )}
            {/* rear door */}
            {rearDoor && (
              <line x1={pad} y1={pad + mm(interior.length)} x2={pad + mm(interior.width)} y2={pad + mm(interior.length)}
                stroke="#D4AF37" strokeWidth="5" strokeDasharray="6 4" />
            )}
          </>
        )}

        {/* modules */}
        {(modules || []).map((m) => {
          const c = catalogOf(m.type);
          const warns = v.perModule[m.id] || [];
          const sel = m.id === selectedId;
          const stroke = warns.length ? "#DC2626" : sel ? "#D4AF37" : "#000";
          return (
            <g key={m.id}
              onPointerDown={(e) => startDrag(e, m)}
              style={{ cursor: editable ? "grab" : "pointer" }}>
              <rect x={pad + mm(m.x)} y={pad + mm(m.y)} width={mm(m.w)} height={mm(m.h)}
                fill={MODULE_FILL[c?.cat] || "#EEE"} stroke={stroke} strokeWidth={sel || warns.length ? 3 : 2} rx="4" />
              <text x={pad + mm(m.x) + 4} y={pad + mm(m.y) + 12} fontSize="9" fontFamily="serif" fontWeight="700" fill="#000">
                {c?.label || m.type}
              </text>
              {mm(m.h) > 26 && (
                <text x={pad + mm(m.x) + 4} y={pad + mm(m.y) + 23} fontSize="7" fontFamily="monospace" fill="#000" opacity="0.7">
                  {m.w}×{m.h}
                </text>
              )}
              {warns.length > 0 && (
                <circle cx={pad + mm(m.x + m.w) - 6} cy={pad + mm(m.y) + 6} r="5" fill="#DC2626" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
